import type {
  Catalog, CatalogResponse, CatalogField, CatalogFieldResponse,
  CronParts, DynamicSourceConfig, CatalogDisplayStatus,
} from '../types';

export function parseCron(cron: string): CronParts {
  const raw = (cron || '').trim();
  // Empty/blank cron splits to [''], which would leak minute:'' — normalize to all-wildcard.
  if (!raw) return { minute: '*', hour: '*', dayOfMonth: '*', month: '*', dayOfWeek: '*' };
  const [minute = '*', hour = '*', dayOfMonth = '*', month = '*', dayOfWeek = '*'] = raw.split(/\s+/);
  return { minute, hour, dayOfMonth, month, dayOfWeek };
}

function mapField(f: CatalogFieldResponse): CatalogField {
  // Coerce the flags: the backend stores them as TINYINT(1), so the JSON may
  // arrive as 0/1 (numbers) rather than booleans. Force real booleans so the
  // runtime value matches the declared `boolean` type and `{flag && <JSX/>}`
  // guards can never leak a stray 0.
  return { id: f.id, name: f.name, type: f.type, required: Boolean(f.is_required), isKey: Boolean(f.is_key), responsePath: f.response_path };
}

function deriveDisplayStatus(r: CatalogResponse): CatalogDisplayStatus {
  const failed = r.sync_status === 'failed' || r.source?.sync_status === 'failed';
  if (r.status === 'active' && failed) return 'sync_failed';
  return r.status;
}

export function catalogMapper(r: CatalogResponse): Catalog {
  const dynamic = r.source
    ? ({
        httpMethod: r.source.http_method,
        url: r.source.url,
        headers: Object.entries(r.source.headers || {}).map(([key, value], i) => ({ id: `h_${i}`, key, value })),
        cron: parseCron(r.source.refresh_cron),
        timeoutMs: r.source.timeout_ms,
        lastSyncedAt: r.source.last_synced_at,
        lastFailedAt: r.source.last_failed_at,
        syncStatus: r.source.sync_status,
      } satisfies DynamicSourceConfig)
    : undefined;
  return {
    id: r.id,
    name: r.name,
    slug: r.slug,
    description: r.description ?? '',
    sourceType: r.source_type === 'dynamic_route' ? 'dynamic' : r.source_type, // 'csv' | 'dynamic' | null
    status: r.status,
    displayStatus: deriveDisplayStatus(r),
    rowCount: r.row_count,
    updatedAt: r.updated_at,
    fields: (r.fields ?? []).map(mapField),
    lastImport: r.last_import
      ? { importedAt: r.last_import.imported_at, okCount: r.last_import.ok_count, errorCount: r.last_import.error_count }
      : undefined,
    dynamicSource: dynamic,
  };
}
